'use client';

import { useEffect, useRef } from 'react';

const analyticsScriptUrl =
	'https://cdn.yourcloudserver.com/content/tagging/ycstm.js';
const analyticsScriptSelector = 'script[data-ycs-analytics-script="true"]';

const brand = {
	primary: '#B40000',
	primaryLight: '#F0C987',
	primaryDark: '#133C55',
	text: 'black',
	black: '#000000',
	white: '#ffffff',
};
const analyticsConfigurationInput = {
	organization: { name: 'USA Blinds' },
	property: { name: 'usablinds.us' },
	config: {
		cookieConsentBanner: {
			colors: {
				background: brand.primary,
				text: brand.white,
				border: brand.primaryDark,
				primaryButtonBackground: brand.primaryDark,
				primaryButtonText: brand.white,
				secondaryButtonBackground: brand.primaryDark,
				secondaryButtonText: brand.white,
				closeButtonBackground: brand.primaryDark,
				closeButtonText: brand.white,
			},
		},
	},
};

function addScriptLoadListeners(scriptElement, resolve, reject) {
	scriptElement.addEventListener(
		'load',
		() => {
			scriptElement.dataset.loaded = 'true';
			resolve();
		},
		{ once: true },
	);
	scriptElement.addEventListener(
		'error',
		() => reject(new Error('Failed to load analytics script')),
		{ once: true },
	);
}

function initializeQueuedAnalyticsConfiguration() {
	window.ycstm = window.ycstm || {
		queue: [],
		ddo: {},
		event: () => {},
	};

	window.ycstm.queue = window.ycstm.queue || [];
	window.ycstm.ddo = window.ycstm.ddo || {};

	window.ycstm.queue.push({
		command: 'config',
		parameters: [analyticsConfigurationInput],
	});

	window.ycstm.ddo.set = (path, dataPayload) => {
		window.ycstm?.queue?.push({
			command: 'ddo.set',
			parameters: [path, dataPayload],
		});
	};

	window.ycstm.event = (eventName, ...args) => {
		window.ycstm?.queue?.push({
			command: 'event',
			parameters: [eventName, ...args],
		});
	};
}

function loadAnalyticsScript() {
	return new Promise((resolve, reject) => {
		const existingScript = document.querySelector(analyticsScriptSelector);

		if (existingScript) {
			if (existingScript.dataset.loaded === 'true') {
				resolve();
				return;
			}

			addScriptLoadListeners(existingScript, resolve, reject);
			return;
		}

		const scriptElement = document.createElement('script');
		scriptElement.src = analyticsScriptUrl;
		scriptElement.async = true;
		scriptElement.defer = true;
		scriptElement.dataset.ycsAnalyticsScript = 'true';
		addScriptLoadListeners(scriptElement, resolve, reject);
		document.head.appendChild(scriptElement);
	});
}

export default function YCSTM() {
	const hasFiredConfiguration = useRef(false);

	/**
	 * This effect is responsible for loading the analytics script and configuring it with the provided input. It waits until the page has fully loaded before starting the process, and it uses requestIdleCallback if available to avoid blocking critical rendering. The configuration is only fired once, and if the component unmounts before the script loads, it will prevent any state updates or configurations from occurring.
	 */
	useEffect(() => {
		let timeoutHandle = null;
		let idleCallbackHandle = null;
		let hasUnmounted = false;

		const configureAnalytics = async () => {
			if (hasFiredConfiguration.current) {
				console.log('Analytics already configured, skipping');
				return;
			}
			hasFiredConfiguration.current = true;

			try {
				await loadAnalyticsScript();
			} catch {
				console.error('Failed to load analytics script');
				hasFiredConfiguration.current = false;
				return;
			}

			if (hasUnmounted) {
				console.log('Component unmounted before configuration');
				return;
			}

			if (window.ycstm?.config) {
				console.log(
					'Configuring analytics with input:',
					analyticsConfigurationInput,
				);
				await window.ycstm.config(analyticsConfigurationInput);
				return;
			} else {
				console.warn(
					'Analytics script loaded but ycstm.config is not available',
				);
			}

			initializeQueuedAnalyticsConfiguration();
		};

		const scheduleAnalyticsConfiguration = () => {
			if (typeof window.requestIdleCallback === 'function') {
				console.log('Using requestIdleCallback to schedule analytics');
				idleCallbackHandle = window.requestIdleCallback(() => {
					void configureAnalytics();
				});
				return;
			}

			console.log('Using setTimeout to schedule analytics');
			timeoutHandle = setTimeout(() => {
				void configureAnalytics();
			}, 0);
		};

		if (document.readyState === 'complete') {
			console.log('Document already loaded, scheduling analytics');
			scheduleAnalyticsConfiguration();
		} else {
			console.log('Waiting for document load event');
			window.addEventListener('load', scheduleAnalyticsConfiguration, {
				once: true,
			});
		}

		return () => {
			hasUnmounted = true;
			window.removeEventListener('load', scheduleAnalyticsConfiguration);

			if (timeoutHandle) {
				clearTimeout(timeoutHandle);
			}

			if (
				idleCallbackHandle !== null &&
				typeof window.cancelIdleCallback === 'function'
			) {
				window.cancelIdleCallback(idleCallbackHandle);
			}
		};
	}, []);
	useEffect(() => {
		let timeoutHandle = null;
		let idleCallbackHandle = null;
		let hasUnmounted = false;

		const configureAnalytics = async () => {
			if (hasFiredConfiguration.current) {
				return;
			}
			hasFiredConfiguration.current = true;

			try {
				await loadAnalyticsScript();
			} catch {
				hasFiredConfiguration.current = false;
				return;
			}

			if (hasUnmounted) {
				return;
			}

			if (window.ycstm?.config) {
				console.log(
					'Configuring analytics with input:',
					analyticsConfigurationInput,
				);
				await window.ycstm.config(analyticsConfigurationInput);
				return;
			} else {
				console.warn(
					'Analytics script loaded but ycstm.config is not available',
				);
			}

			initializeQueuedAnalyticsConfiguration();
		};

		const scheduleAnalyticsConfiguration = () => {
			if (typeof window.requestIdleCallback === 'function') {
				idleCallbackHandle = window.requestIdleCallback(() => {
					void configureAnalytics();
				});
				return;
			}

			timeoutHandle = setTimeout(() => {
				void configureAnalytics();
			}, 0);
		};

		if (document.readyState === 'complete') {
			scheduleAnalyticsConfiguration();
		} else {
			window.addEventListener('load', scheduleAnalyticsConfiguration, {
				once: true,
			});
		}

		return () => {
			hasUnmounted = true;
			window.removeEventListener('load', scheduleAnalyticsConfiguration);

			if (timeoutHandle) {
				clearTimeout(timeoutHandle);
			}

			if (
				idleCallbackHandle !== null &&
				typeof window.cancelIdleCallback === 'function'
			) {
				window.cancelIdleCallback(idleCallbackHandle);
			}
		};
	}, []);

	return null;
}
